import Card from "@material-ui/core/Card";
import Collapse from "@material-ui/core/Collapse";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useAppState } from "../../../../../../../../common/appState";
import { fetchPost } from "../../../../../../../../common/util/fetch";
import ShurikenProgress from "../../../../../../../shared/Animations/ShurikenProgress";
import {
    User,
    UserForMessageList,
    UserForRanking,
} from "../../../../../../../shared/User/types";
import { InitialView } from "../../../MyPageTop/types";
import { OpenableCard } from "../OpenableCard";
import {
    MessagedUserRecord,
    myPageMessageCardScrollableContainerHeight,
} from "./MessageUserRecord";

export function MessagesCard({
    user,
    initialView,
    scrollableContainerRef,
}: {
    user: User;
    initialView?: InitialView;
    scrollableContainerRef: RefObject<HTMLDivElement>;
}) {
    const [open, setOpen] = useState(false);
    const [unreadMessages] = useAppState("unreadMessages");
    const [users, setUsers] = useState<UserForRanking[] | "loading">("loading");
    const [isTemporallyData, setIsTemporallyData] = useState(false);

    const strUserIdsWhoSentUnreadMessages = useMemo(
        () =>
            Array.from(new Set(unreadMessages.map(m => m.fromUserId)))
                .sort()
                .join(","),
        unreadMessages
    );

    const { userId } = user;
    useEffect(() => {
        if (!open) {
            return;
        }
        sFetchUsersWhoSentMessages(userId, setUsers, setIsTemporallyData);
    }, [userId, open, strUserIdsWhoSentUnreadMessages]);

    const c = useStyles();

    const usersForMessageList =
        users === "loading"
            ? "loading"
            : users
                  .map(u => ({
                      ...u,
                      unreadMessageCount: unreadMessages.filter(
                          m => m.fromUserId === u.userId
                      ).length,
                  }))
                  .sort((a, b) => b.unreadMessageCount - a.unreadMessageCount);

    return (
        <OpenableCard
            title={
                <span>
                    Messages
                    {unreadMessages.length > 0 && (
                        <>
                            {": "}
                            <span className={c.titleNumber}>
                                {unreadMessages.length}
                            </span>
                        </>
                    )}
                </span>
            }
            icon={<SendIcon />}
            saveKey="MyPageMessagesCard"
            id="MyPageMessagesCard"
            open={open}
            setOpen={setOpen}
            alwaysShowIcon
            alwaysShowTitle
            initiallyOpenedId={initialView}
            exclamationMark={unreadMessages.length > 0}
            scrollableContainerRef={scrollableContainerRef}
        >
            <MessagedUserList
                messagedUsers={usersForMessageList}
                isTemporallyData={isTemporallyData}
            />
        </OpenableCard>
    );
}
const useStyles = makeStyles(theme => ({
    titleNumber: { color: theme.palette.secondary.main },
}));

const usersWhoSentMessagesKey = "usersWhoSentMessagesListKey";
async function sFetchUsersWhoSentMessages(
    myUserId: number,
    setUsers: (users: UserForRanking[] | "loading") => void,
    setIsTemporallyData: (isTemporallyData: boolean) => void
) {
    const savedData = localStorage.getItem(usersWhoSentMessagesKey);
    if (savedData) {
        setUsers(JSON.parse(savedData)); // 保存されたものがあれば、一旦ここでセットする。しかし、以下で最新情報の取得も継続する。
        setIsTemporallyData(true);
    }

    // ブロックしないように、Asyncの即時関数として実行
    (async () => {
        const usersForRanking = await fetchPost<number, UserForRanking[]>(
            "api/Message/GetUsersWhoSentMessages",
            myUserId
        );

        if ("error" in usersForRanking) {
            setUsers([]);
            setIsTemporallyData(false);
            return;
        }
        setUsers(usersForRanking);
        setIsTemporallyData(false);

        localStorage.setItem(
            usersWhoSentMessagesKey,
            JSON.stringify(usersForRanking)
        );
    })();
}

function MessagedUserList({
    messagedUsers,
    isTemporallyData,
}: {
    messagedUsers: UserForMessageList[] | "loading";
    isTemporallyData: boolean;
}) {
    const c = useListStyles();

    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const [scrollTop, setScrollTop] = useState(0);
    useEffect(() => {
        const el = scrollableContainerRef.current;
        if (!el) {
            return;
        }
        const onScroll = () => {
            setScrollTop(el.scrollTop);
        };
        setScrollTop(el.scrollTop);
        el.addEventListener("scroll", onScroll);
        return () => {
            el.removeEventListener("scroll", onScroll);
        };
    }, []);

    return (
        <Card className={c.container} ref={scrollableContainerRef}>
            {messagedUsers === "loading" ? (
                <ShurikenProgress size={30} className={c.shuriken} />
            ) : (
                <>
                    <Collapse in={isTemporallyData} timeout={500}>
                        <ShurikenProgress
                            size={30}
                            className={c.shurikenInList}
                        />
                    </Collapse>

                    {messagedUsers.map((messagedUser, i) => (
                        <MessagedUserRecord
                            key={messagedUser.userId}
                            messagedUser={messagedUser}
                            colorNumber={i + 1}
                            scrollTop={scrollTop}
                            index={i}
                        />
                    ))}

                    {messagedUsers.length === 0 && (
                        <div className={c.noData}>No Data</div>
                    )}
                </>
            )}
        </Card>
    );
}
const useListStyles = makeStyles(theme => ({
    container: {
        backgroundColor: theme.palette.grey[100],
        height: myPageMessageCardScrollableContainerHeight,
        maxHeight: myPageMessageCardScrollableContainerHeight,
        flex: 1,
        fontSize: "large",
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        margin: 5,
    },
    noData: {
        width: "100%",
        height: 305,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: theme.palette.grey[600],
    },
    shuriken: { marginTop: 135 },
    shurikenInList: { margin: "15px 0 10px" },
}));
